/**
 * Coaching Recommendations Panel Component
 * AI-powered coaching suggestions for sales reps
 * Phase 6.5 - AI Management Tools
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Collapse,
  Divider,
  LinearProgress,
  Skeleton
} from '@mui/material';
import {
  Psychology as AIIcon,
  School as CoachingIcon,
  TrendingUp as ImprovementIcon,
  Star as StrengthIcon,
  Warning as WeaknessIcon,
  CheckCircle as ActionIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PersonAdd as MentorIcon
} from '@mui/icons-material';

export interface CoachingRecommendation {
  id: string;
  salesRepId: string;
  salesRepName: string;
  area: 'conversion' | 'response_time' | 'deal_cycle' | 'activity' | 'communication';
  priority: 'high' | 'medium' | 'low';
  currentScore: number;
  targetScore: number;
  strengths: string[];
  weaknesses: string[];
  actionItems: string[];
  suggestedMentor?: string;
  estimatedImpact: string;
  timeframe: string;
}

export interface CoachingRecommendationsPanelProps {
  recommendations: CoachingRecommendation[];
  loading?: boolean;
}

const CoachingRecommendationsPanel: React.FC<CoachingRecommendationsPanelProps> = ({
  recommendations,
  loading = false
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Get area label and color
  const getAreaInfo = (area: CoachingRecommendation['area']) => {
    const areaMap = {
      conversion: { label: 'Conversion Rate', color: 'primary' as const },
      response_time: { label: 'Response Time', color: 'info' as const },
      deal_cycle: { label: 'Deal Cycle', color: 'warning' as const },
      activity: { label: 'Activity Level', color: 'success' as const },
      communication: { label: 'Communication', color: 'secondary' as const }
    };
    return areaMap[area];
  };

  // Get priority color
  const getPriorityColor = (priority: CoachingRecommendation['priority']) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CoachingIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            AI Coaching Recommendations
          </Typography>
        </Box>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
          <CoachingIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="600">
            AI Coaching Recommendations
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {recommendations.length} personalized coaching plans
          </Typography>
        </Box>
      </Box>

      {/* Recommendations List */}
      <List disablePadding>
        {recommendations.map((rec, index) => {
          const isExpanded = expandedId === rec.id;
          const areaInfo = getAreaInfo(rec.area);
          const progress = (rec.currentScore / rec.targetScore) * 100;

          return (
            <Paper
              key={rec.id}
              sx={{
                mb: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: isExpanded ? `${areaInfo.color}.main` : 'divider',
                transition: 'all 0.2s'
              }}
            >
              {/* Main Content */}
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => handleToggleExpand(rec.id)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: `${areaInfo.color}.light`, color: `${areaInfo.color}.main` }}>
                    <AIIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        {rec.salesRepName}
                      </Typography>
                      <Chip
                        label={areaInfo.label}
                        size="small"
                        color={areaInfo.color}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={rec.priority.toUpperCase()}
                        size="small"
                        color={getPriorityColor(rec.priority)}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Progress to Target
                        </Typography>
                        <Typography variant="caption" fontWeight="600">
                          {rec.currentScore} / {rec.targetScore}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        color={areaInfo.color}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Estimated Impact: {rec.estimatedImpact} | Timeframe: {rec.timeframe}
                      </Typography>
                    </Box>
                  }
                />
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </ListItem>

              {/* Expanded Details */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Strengths */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <StrengthIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      <Typography variant="subtitle2" fontWeight="600">
                        Strengths
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rec.strengths.map((strength, idx) => (
                        <Chip
                          key={idx}
                          label={strength}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Areas for Improvement */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <WeaknessIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                      <Typography variant="subtitle2" fontWeight="600">
                        Areas for Improvement
                      </Typography>
                    </Box>
                    <List dense disablePadding>
                      {rec.weaknesses.map((weakness, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            • {weakness}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Action Items */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <ActionIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" fontWeight="600">
                        Recommended Actions
                      </Typography>
                    </Box>
                    <List dense disablePadding>
                      {rec.actionItems.map((action, idx) => (
                        <ListItem
                          key={idx}
                          sx={{
                            py: 0.5,
                            px: 1.5,
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            mb: 0.5
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {idx + 1}. {action}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Suggested Mentor */}
                  {rec.suggestedMentor && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        bgcolor: 'info.50',
                        borderRadius: 1,
                        mb: 2
                      }}
                    >
                      <MentorIcon sx={{ color: 'info.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Suggested Mentor
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="info.main">
                          {rec.suggestedMentor}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" fullWidth startIcon={<CoachingIcon />}>
                      Start Coaching Plan
                    </Button>
                    <Button variant="outlined" size="small" fullWidth>
                      Schedule 1-on-1
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </List>

      {/* Empty State */}
      {recommendations.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <CoachingIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No coaching recommendations available. All team members are performing well!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CoachingRecommendationsPanel;
